import ReactGA from 'react-ga4';
import posthog from 'posthog-js';
import { getAnalyticsTrafficType } from './analyticsTraffic';
import { optionalTelemetryAllowed } from './appEnvironment';

export const ANALYTICS_EVENTS = Object.freeze({
  landingPageView: 'landing_page_view',
  plannerStarted: 'planner_started',
  planCompleted: 'plan_completed',
  affiliateOfferViewed: 'affiliate_offer_viewed',
  affiliateClicked: 'affiliate_clicked',
  affiliateConversionObserved: 'affiliate_conversion_observed',
  shareCreated: 'share_created',
  shareOpened: 'share_opened',
});

const EVENT_SCHEMA_VERSION = 1;
const MAX_PROPERTY_STRING_LENGTH = 240;
const MAX_ARRAY_LENGTH = 12;
const ATTRIBUTION_STORAGE_KEY = 'odysseyai.analytics.first_touch.v1';
const SENSITIVE_PROPERTY_NAME = /(?:^|_)(?:email|phone|password|secret|token|prompt|query|message|free_text|url|href)(?:_|$)/i;
const ATTRIBUTION_KEYS = Object.freeze({
  utm_source: 'source',
  utm_medium: 'medium',
  utm_campaign: 'campaign',
  utm_content: 'content',
  ref: 'referral_id',
  share_id: 'share_id',
});

const hasBrowserContext = () => (
  typeof window !== 'undefined' && typeof window.location !== 'undefined'
);

const stripControlCharacters = (value) => Array.from(value, (character) => {
  const codePoint = character.codePointAt(0);
  return codePoint <= 31 || codePoint === 127 ? ' ' : character;
}).join('');

const cleanString = (value, maxLength = MAX_PROPERTY_STRING_LENGTH) => {
  if (typeof value !== 'string') return undefined;
  const cleaned = stripControlCharacters(value)
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned ? cleaned.slice(0, maxLength) : undefined;
};

const cleanPropertyValue = (value) => {
  if (typeof value === 'string') return cleanString(value);
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (Array.isArray(value)) {
    const cleaned = value
      .slice(0, MAX_ARRAY_LENGTH)
      .map((item) => (
        typeof item === 'string'
          ? cleanString(item, 80)
          : (typeof item === 'number' && Number.isFinite(item)) || typeof item === 'boolean'
            ? item
            : undefined
      ))
      .filter((item) => item !== undefined);
    return cleaned.length ? cleaned : undefined;
  }
  return undefined;
};

/**
 * Keep analytics payloads useful for funnels without allowing raw prompts,
 * URLs containing credentials, or arbitrary nested API objects into vendors.
 */
export const sanitizeAnalyticsProperties = (properties = {}) => {
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
    return {};
  }

  return Object.entries(properties).reduce((safeProperties, [key, value]) => {
    if (!/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/.test(key) || SENSITIVE_PROPERTY_NAME.test(key)) {
      return safeProperties;
    }
    const cleaned = cleanPropertyValue(value);
    if (cleaned !== undefined) safeProperties[key] = cleaned;
    return safeProperties;
  }, {});
};

const sanitizeAttribution = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.values(ATTRIBUTION_KEYS).reduce((attribution, propertyKey) => {
    const cleaned = cleanString(value[propertyKey] || '', 120);
    if (cleaned) attribution[propertyKey] = cleaned;
    return attribution;
  }, {});
};

const readQueryAttribution = () => {
  if (!hasBrowserContext()) return {};
  const searchParams = new URLSearchParams(window.location.search || '');
  const attribution = {};
  Object.entries(ATTRIBUTION_KEYS).forEach(([queryKey, propertyKey]) => {
    const value = cleanString(searchParams.get(queryKey) || '', 120);
    if (value) attribution[propertyKey] = value;
  });
  return attribution;
};

const readStoredAttribution = () => {
  if (!hasBrowserContext()) return {};
  try {
    const value = window.sessionStorage?.getItem(ATTRIBUTION_STORAGE_KEY);
    return value ? sanitizeAttribution(JSON.parse(value)) : {};
  } catch {
    return {};
  }
};

const storeAttribution = (attribution) => {
  if (!hasBrowserContext() || !Object.keys(attribution).length) return;
  try {
    window.sessionStorage?.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Analytics attribution must never interrupt planning in restricted browsers.
  }
};

const readAttribution = () => {
  const storedAttribution = readStoredAttribution();
  if (Object.keys(storedAttribution).length) return storedAttribution;

  const queryAttribution = readQueryAttribution();
  storeAttribution(queryAttribution);
  return queryAttribution;
};

const readReferrerDomain = () => {
  if (typeof document === 'undefined' || !document.referrer) return undefined;
  try {
    return cleanString(new URL(document.referrer).hostname, 120);
  } catch {
    return undefined;
  }
};

export const getAnalyticsContext = () => {
  const attribution = readAttribution();
  const context = {
    event_schema_version: EVENT_SCHEMA_VERSION,
    traffic_type: getAnalyticsTrafficType(),
    ...attribution,
    acquisition_source: attribution.source,
    acquisition_medium: attribution.medium,
    acquisition_campaign: attribution.campaign,
    acquisition_content: attribution.content,
  };

  if (hasBrowserContext()) {
    // Deliberately exclude query strings and hashes: shared trip fragments and
    // third-party callback parameters do not belong in analytics payloads.
    context.page_path = cleanString(window.location.pathname || '/', 160) || '/';
  }
  const referrerDomain = readReferrerDomain();
  if (referrerDomain) context.referrer_domain = referrerDomain;
  return context;
};

export const trackEvent = (eventName, properties = {}) => {
  if (!optionalTelemetryAllowed()) return false;
  const safeEventName = cleanString(eventName, 64);
  if (!safeEventName) return false;
  const eventProperties = {
    ...getAnalyticsContext(),
    ...sanitizeAnalyticsProperties(properties),
    traffic_type: getAnalyticsTrafficType(),
    event_schema_version: EVENT_SCHEMA_VERSION,
  };

  try {
    ReactGA.event(safeEventName, eventProperties);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[analytics] GA event failed', safeEventName, error?.name || 'Error');
    }
  }

  try {
    if (process.env.REACT_APP_POSTHOG_API_KEY) {
      posthog.capture(safeEventName, eventProperties);
    }
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[analytics] PostHog event failed', safeEventName, error?.name || 'Error');
    }
  }
  return true;
};

const countGroundedVenues = (itinerary = {}) => Object.values(itinerary?.venues || {})
  .filter((venue) => Boolean(venue?.source_url || venue?.source))
  .length;

export const getItineraryAnalyticsProperties = (itinerary = {}) => ({
  destination: cleanString(itinerary?.destination || '', 80),
  duration_days: Array.isArray(itinerary?.days) ? itinerary.days.length : undefined,
  venue_count: Object.keys(itinerary?.venues || {}).length,
  grounded_venue_count: countGroundedVenues(itinerary),
  completion_status: cleanString(itinerary?.completion_status || '', 40),
});

export const trackLandingPageView = (properties = {}) => trackEvent(
  ANALYTICS_EVENTS.landingPageView,
  { page_name: 'home', ...properties },
);

export const trackPlannerStarted = (properties = {}) => trackEvent(
  ANALYTICS_EVENTS.plannerStarted,
  { entry_point: 'unknown', ...properties },
);

export const trackPlanCompleted = ({ itinerary, ...properties } = {}) => trackEvent(
  ANALYTICS_EVENTS.planCompleted,
  { ...getItineraryAnalyticsProperties(itinerary), ...properties },
);

export const trackAffiliateOfferViewed = (properties = {}) => trackEvent(
  ANALYTICS_EVENTS.affiliateOfferViewed,
  properties,
);

export const trackAffiliateClicked = (properties = {}) => trackEvent(
  ANALYTICS_EVENTS.affiliateClicked,
  properties,
);

export const trackAffiliateConversionObserved = ({ revenue, currency, ...properties } = {}) => {
  const numericRevenue = Number(revenue);
  return trackEvent(ANALYTICS_EVENTS.affiliateConversionObserved, {
    ...properties,
    revenue: Number.isFinite(numericRevenue) && numericRevenue >= 0 ? numericRevenue : undefined,
    currency: cleanString(currency || '', 8)?.toUpperCase(),
  });
};

export const trackShareCreated = (properties = {}) => trackEvent(
  ANALYTICS_EVENTS.shareCreated,
  properties,
);

export const trackShareOpened = (properties = {}) => trackEvent(
  ANALYTICS_EVENTS.shareOpened,
  properties,
);
